import { useCallback, useMemo, useState } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { getProgress, patchProgress } from '../listenApi.js';
import {
  chapterKey,
  STATUS_RANK,
  type ChapterProgress,
  type ChapterRef,
  type ChapterStatus,
  type ProgressEntry,
} from '../types.js';

/** Derivación de estado de capítulo a partir del progreso (+ "escuchando"). */
export function deriveStatus(progress: ChapterProgress | null, isActive: boolean): ChapterStatus {
  if (progress?.conversedAt) return 'conversado';
  if (progress?.reviewedAt) return 'repasado';
  if (progress?.listenedAt) return 'escuchado';
  if ((progress && progress.lastPositionSec > 0) || isActive) return 'escuchando';
  return 'no-escuchado';
}

export interface UseProgressMapReturn {
  progressMap: Record<string, ChapterProgress>;
  /** Lo escriben también `loadRendition` (progreso del fetch) y `onEnded`. */
  setProgressMap: Dispatch<SetStateAction<Record<string, ChapterProgress>>>;
  statuses: Record<string, ChapterStatus>;
  statusOf: (ref: ChapterRef) => ChapterStatus;
  progressOf: (ref: ChapterRef) => ChapterProgress | null;
  /** Devuelve además las filas crudas (ordenadas por recencia) para `pickListenResume`. */
  refreshProgress: (book?: string) => Promise<ProgressEntry[]>;
  markConversed: (ref?: ChapterRef) => Promise<void>;
  markReviewed: (ref?: ChapterRef) => Promise<void>;
}

/**
 * Mapa de progreso por capítulo + estados derivados + marcas optimistas
 * (never-downgrade local; el server también es never-downgrade).
 */
export function useProgressMap(args: {
  /** Capítulo abierto (para "escuchando" del activo y default de las marcas). */
  refRef: RefObject<ChapterRef | null>;
  ref: ChapterRef | null;
  playing: boolean;
  pos: number;
}): UseProgressMapReturn {
  const { refRef, ref, playing, pos } = args;

  const [progressMap, setProgressMap] = useState<Record<string, ChapterProgress>>({});

  const refreshProgress = useCallback(async (bookSlug?: string): Promise<ProgressEntry[]> => {
    try {
      const { progress } = await getProgress(bookSlug);
      setProgressMap((m) => {
        const next = { ...m };
        for (const p of progress) {
          next[chapterKey(p)] = {
            lastPositionSec: p.lastPositionSec,
            lastCefr: p.lastCefr,
            listenedAt: p.listenedAt,
            reviewedAt: p.reviewedAt,
            conversedAt: p.conversedAt,
          };
        }
        return next;
      });
      return progress;
    } catch {
      /* sin sesión / vacío → statuses quedan en no-escuchado */
      return [];
    }
  }, []);

  const progressOf = useCallback(
    (r: ChapterRef): ChapterProgress | null => progressMap[chapterKey(r)] ?? null,
    [progressMap],
  );

  const statusOf = useCallback(
    (r: ChapterRef): ChapterStatus => {
      const key = chapterKey(r);
      const isActive = refRef.current ? chapterKey(refRef.current) === key : false;
      return deriveStatus(progressMap[key] ?? null, isActive && (playing || pos > 0));
    },
    [progressMap, playing, pos, refRef],
  );

  const statuses = useMemo<Record<string, ChapterStatus>>(() => {
    const out: Record<string, ChapterStatus> = {};
    const activeKey = ref ? chapterKey(ref) : null;
    for (const [key, p] of Object.entries(progressMap)) {
      out[key] = deriveStatus(p, key === activeKey && (playing || pos > 0));
    }
    if (activeKey && !out[activeKey]) {
      out[activeKey] = deriveStatus(null, playing || pos > 0);
    }
    return out;
  }, [progressMap, ref, playing, pos]);

  const markConversed = useCallback(
    async (target?: ChapterRef): Promise<void> => {
      const r = target ?? refRef.current;
      if (!r) return;
      const key = chapterKey(r);
      // Optimista (never-downgrade local).
      setProgressMap((m) => {
        const prev = m[key];
        if (prev && STATUS_RANK[deriveStatus(prev, false)] >= STATUS_RANK.conversado) return m;
        return {
          ...m,
          [key]: {
            lastPositionSec: prev?.lastPositionSec ?? 0,
            lastCefr: prev?.lastCefr ?? null,
            listenedAt: prev?.listenedAt ?? new Date().toISOString(),
            reviewedAt: prev?.reviewedAt ?? null,
            conversedAt: new Date().toISOString(),
          },
        };
      });
      await patchProgress({ ...r, conversed: true }).catch(() => undefined);
    },
    [refRef],
  );

  // Completar una actividad de repaso (19.G) marca "repasado". Mismo patrón
  // optimista never-downgrade que `markConversed`.
  const markReviewed = useCallback(
    async (target?: ChapterRef): Promise<void> => {
      const r = target ?? refRef.current;
      if (!r) return;
      const key = chapterKey(r);
      setProgressMap((m) => {
        const prev = m[key];
        if (prev?.reviewedAt) return m;
        return {
          ...m,
          [key]: {
            lastPositionSec: prev?.lastPositionSec ?? 0,
            lastCefr: prev?.lastCefr ?? null,
            listenedAt: prev?.listenedAt ?? new Date().toISOString(),
            reviewedAt: new Date().toISOString(),
            conversedAt: prev?.conversedAt ?? null,
          },
        };
      });
      await patchProgress({ ...r, reviewed: true }).catch(() => undefined);
    },
    [refRef],
  );

  return {
    progressMap,
    setProgressMap,
    statuses,
    statusOf,
    progressOf,
    refreshProgress,
    markConversed,
    markReviewed,
  };
}
